import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Hook to manage modal blocking during async operations.
 * Tracks when ActionButtons or other async operations are in progress
 * and provides props to block modal cancellation.
 */
export const useModalBlocking = () => {
  const [isBlocking, setIsBlocking] = useState(false);
  const blockingOperations = useRef(new Set<string>());

  /**
   * Start blocking the modal with a unique operation ID
   */
  const startBlocking = useCallback((operationId = "default") => {
    blockingOperations.current.add(operationId);
    setIsBlocking(true);
  }, []);

  /**
   * Stop blocking for a specific operation ID
   */
  const stopBlocking = useCallback((operationId = "default") => {
    blockingOperations.current.delete(operationId);
    if (blockingOperations.current.size === 0) {
      setIsBlocking(false);
    }
  }, []);

  /**
   * Create a wrapped onClick handler that manages blocking state
   */
  const createBlockingHandler = useCallback(
    <T extends unknown[]>(
      handler: (...args: T) => Promise<void> | void,
      operationId?: string,
    ) => {
      return async (...args: T) => {
        const opId = operationId || "default";
        startBlocking(opId);
        try {
          await handler(...args);
        } finally {
          stopBlocking(opId);
        }
      };
    },
    [startBlocking, stopBlocking],
  );

  /**
   * Modal props to prevent cancellation during blocking operations
   */
  const modalProps = {
    closable: !isBlocking,
    maskClosable: !isBlocking,
  };

  /**
   * Get a version of onCancel that respects blocking state
   */
  const getBlockableOnCancel = useCallback(
    (originalOnCancel: () => void) => {
      return isBlocking ? undefined : originalOnCancel;
    },
    [isBlocking],
  );

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      blockingOperations.current.clear();
    };
  }, []);

  return {
    isBlocking,
    startBlocking,
    stopBlocking,
    createBlockingHandler,
    modalProps,
    getBlockableOnCancel,
  };
};
